import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../app/AuthContext";

export function AdminDashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.admin.dashboard>> | null>(null);
  const [discoveryQueued, setDiscoveryQueued] = useState(false);
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [imageRepairLoading, setImageRepairLoading] = useState(false);
  const [imageRepairMessage, setImageRepairMessage] = useState<string | null>(null);

  const load = () => {
    if (!user || (user.role !== "admin" && user.role !== "super_admin")) return;
    api.admin.dashboard().then(setData).catch(() => setData(null));
  };

  useEffect(() => {
    load();
  }, [user]);

  if (!user || (user.role !== "admin" && user.role !== "super_admin")) {
    return <div className="container empty-state">Admin access required.</div>;
  }

  return (
    <div className="container" style={{ paddingBlock: "2rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
        <h2>Admin</h2>
        <button
          className="primary"
          disabled={discoveryLoading}
          onClick={async () => {
            setDiscoveryLoading(true);
            setDiscoveryError(null);
            try {
              await api.admin.discoveryRun();
              setDiscoveryQueued(true);
              load();
            } catch (err) {
              setDiscoveryError(err instanceof Error ? err.message : "Discovery run failed");
            } finally {
              setDiscoveryLoading(false);
            }
          }}
        >
          {discoveryLoading ? "Queueing..." : "Run discovery now"}
        </button>
      </div>
      {discoveryQueued && (
        <p style={{ color: "var(--fg-muted)", marginTop: "0.75rem" }}>
          Discovery queued. Source scans will appear in Sources as queue workers process them.
        </p>
      )}
      {discoveryError && (
        <p style={{ color: "var(--danger)", marginTop: "0.75rem" }}>
          {discoveryError}
        </p>
      )}

      {data?.operationalMonitor && (
        <section className={`admin-monitor admin-monitor--${data.operationalMonitor.status}`}>
          <div className="admin-monitor__head">
            <div>
              <span>Operational monitoring</span>
              <h3>{data.operationalMonitor.status === "healthy" ? "Healthy" : data.operationalMonitor.status === "watch" ? "Watch" : "Needs attention"}</h3>
              <p>{data.operationalMonitor.summary}</p>
            </div>
            <button
              disabled={qualityLoading}
              onClick={async () => {
                setQualityLoading(true);
                try {
                  await api.admin.launchQualityReport();
                  load();
                } finally {
                  setQualityLoading(false);
                }
              }}
            >
              {qualityLoading ? "Checking..." : "Refresh health"}
            </button>
          </div>
          <div className="admin-monitor__checks">
            {data.operationalMonitor.checks.map((check) => (
              <Link key={check.id} to={check.href} className={`admin-monitor__check is-${check.severity}`}>
                <span>{check.label}</span>
                <b>{check.value}</b>
                <small>{check.action}</small>
              </Link>
            ))}
          </div>
          {data.operationalMonitor.latestIncident && (
            <p className="admin-monitor__incident">
              Latest incident: {data.operationalMonitor.latestIncident.event_type} · {data.operationalMonitor.latestIncident.message} · {new Date(data.operationalMonitor.latestIncident.created_at).toLocaleString()}
            </p>
          )}
        </section>
      )}

      {data && (
        <section className="signal-cards" style={{ marginTop: "2rem" }}>
          <Stat label="New candidates today"   value={data.counts.newCandidatesToday} />
          <Stat label="Published today"        value={data.counts.publishedToday} />
          <Stat label="Review queue"           value={data.counts.reviewQueueSize} link="/admin/candidates?status=review" />
          <Stat label="Failed jobs"            value={data.counts.failedJobs} />
          <Stat label="Total published brands" value={data.counts.totalPublishedBrands} link="/admin/brands?status=published" />
          <Stat label="Rejected search leads 7d" value={data.counts.rejectedSearchLeads7d} link="/admin/sources" />
          <Stat label="Suppressed domains 7d" value={data.counts.suppressedDomains7d} link="/admin/sources" />
          <Stat label="Source penalties 7d" value={data.counts.sourcePenalties7d} link="/admin/sources" />
        </section>
      )}

      {data?.latestQualityReport && (
        <section className="admin-quality-report" style={{ marginTop: "2rem" }}>
          <div className="section__head" style={{ marginBottom: "0.75rem" }}>
            <h3 style={{ fontFamily: "var(--font-serif)" }}>Launch quality report</h3>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
              <span style={{ color: "var(--fg-subtle)", fontFamily: "var(--font-mono)", fontSize: "0.75rem" }}>
                {new Date(data.latestQualityReport.created_at).toLocaleString()}
              </span>
              <button
                disabled={qualityLoading}
                onClick={async () => {
                  setQualityLoading(true);
                  try {
                    await api.admin.launchQualityReport();
                    load();
                  } finally {
                    setQualityLoading(false);
                  }
                }}
              >
                {qualityLoading ? "Updating..." : "Update report"}
              </button>
              <button
                disabled={imageRepairLoading}
                onClick={async () => {
                  setImageRepairLoading(true);
                  setImageRepairMessage(null);
                  try {
                    const result = await api.admin.repairWeakImages();
                    setImageRepairMessage(`Repaired ${result.repaired}, skipped ${result.skipped}.`);
                    load();
                  } finally {
                    setImageRepairLoading(false);
                  }
                }}
              >
                {imageRepairLoading ? "Repairing..." : "Repair weak images"}
              </button>
            </div>
          </div>
          {data.latestQualityReport.warnings.length > 0 ? (
            <div className="admin-quality-report__warnings">
              {data.latestQualityReport.warnings.map((warning) => <span key={warning}>{warning}</span>)}
            </div>
          ) : (
            <p className="admin-quality-report__ok">{data.latestQualityReport.message}</p>
          )}
          <div className="admin-quality-report__grid">
            <QualityMetric label="Published" value={data.latestQualityReport.publishedTotal} />
            <QualityMetric label="New 24h" value={data.latestQualityReport.published24h} />
            <QualityMetric label="New 7d" value={data.latestQualityReport.published7d} />
            <QualityMetric label="Missing images" value={data.latestQualityReport.missingImages} />
            <QualityMetric label="Weak images" value={data.latestQualityReport.weakImages} />
            <QualityMetric label="Logo plates" value={data.latestQualityReport.logoPlateImages} />
            <QualityMetric label="Review queue" value={data.latestQualityReport.reviewQueue} />
            <QualityMetric label="Social failed" value={data.latestQualityReport.social.failed} />
          </div>
          {data.latestQualityReport.missingImageBrands.length > 0 && (
            <p className="admin-quality-report__note">
              Missing images: {data.latestQualityReport.missingImageBrands.map((brand) => (
                <span key={brand.slug}>
                  <Link to={`/admin/brands?status=published&q=${encodeURIComponent(brand.name)}`}>{brand.name}</Link>{" "}
                </span>
              ))}
            </p>
          )}
          {data.latestQualityReport.weakImageBrands.length > 0 && (
            <p className="admin-quality-report__note">
              Weak images: {data.latestQualityReport.weakImageBrands.map((brand) => (
                <span key={brand.slug}>
                  <Link to={`/admin/brands?status=published&q=${encodeURIComponent(brand.name)}`}>{brand.name}</Link>{" "}
                </span>
              ))}
            </p>
          )}
          {data.latestQualityReport.logoPlateImageBrands.length > 0 && (
            <p className="admin-quality-report__note">
              Logo plates: {data.latestQualityReport.logoPlateImageBrands.map((brand) => (
                <span key={brand.slug}>
                  <Link to={`/admin/brands?status=published&q=${encodeURIComponent(brand.name)}`}>{brand.name}</Link>{" "}
                </span>
              ))}
            </p>
          )}
          {imageRepairMessage && <p className="admin-quality-report__note">{imageRepairMessage}</p>}
        </section>
      )}

      {data && !data.latestQualityReport && (
        <section className="admin-quality-report" style={{ marginTop: "2rem" }}>
          <div className="section__head" style={{ marginBottom: 0 }}>
            <h3 style={{ fontFamily: "var(--font-serif)" }}>Launch quality report</h3>
            <button
              className="primary"
              disabled={qualityLoading}
              onClick={async () => {
                setQualityLoading(true);
                try {
                  await api.admin.launchQualityReport();
                  load();
                } finally {
                  setQualityLoading(false);
                }
              }}
            >
              {qualityLoading ? "Creating..." : "Create report"}
            </button>
          </div>
        </section>
      )}

      <div className="section__head" style={{ marginTop: "3rem" }}><h2 style={{ fontSize: "1.25rem" }}>Workspaces</h2></div>
      <div className="brand-grid admin-workspaces-grid">
        <NavCard to="/admin/candidates?status=review" title="Candidate queue" desc="Brands waiting on human review." />
        <NavCard to="/admin/candidates?status=new" title="New candidates" desc="Recently discovered, pre-pipeline." />
        <NavCard to="/admin/brands" title="Brands" desc="Edit, merge, and remove brands." />
        <NavCard to="/admin/tags" title="Tags" desc="Manage the taxonomy." />
        <NavCard to="/admin/sources" title="Sources" desc="Manage discovery sources and trigger scans." />
        <NavCard to="/admin/social" title="Social queue" desc="Instagram-ready posts generated from strong radar signals." />
        <NavCard to="/admin/import" title="Import CSV" desc="Bulk-seed brands from a CSV file." />
      </div>

      {data && data.recentAutoPublished.length > 0 && (
        <section style={{ marginTop: "3rem" }}>
          <div className="section__head">
            <h3 style={{ fontFamily: "var(--font-serif)" }}>Recently auto-published</h3>
            <Link to="/brands?fresh=week&sort=recent">Public fresh view →</Link>
          </div>
          <div className="admin-table-wrap"><table className="admin-table">
            <thead><tr><th>Brand</th><th>Where</th><th>Auto</th><th>Disc</th><th>Main</th><th>When</th><th></th></tr></thead>
            <tbody>
              {data.recentAutoPublished.map((brand) => (
                <tr key={brand.id}>
                  <td>
                    <Link to={`/brands/${brand.slug}`}>{brand.name}</Link>
                    <div style={{ color: "var(--fg-subtle)", fontSize: "0.78rem" }}>{brand.canonical_domain ?? "—"}</div>
                  </td>
                  <td>{[brand.region, brand.country].filter(Boolean).join(", ") || "—"}</td>
                  <td>{brand.auto_publish_score ?? brand.log_score ?? "—"}</td>
                  <td>{brand.discovery_score ?? "—"}</td>
                  <td>{brand.mainstream_score ?? "—"}</td>
                  <td>{new Date(brand.logged_at).toLocaleString()}</td>
                  <td><Link to={`/admin/brands/${brand.id}`}>Edit →</Link></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </section>
      )}

      {data && data.crowdPulse.length > 0 && (
        <section style={{ marginTop: "3rem" }}>
          <h3 style={{ fontFamily: "var(--font-serif)", marginBottom: "0.75rem" }}>Crowd pulse</h3>
          <div className="admin-table-wrap"><table className="admin-table">
            <thead><tr><th>Brand</th><th>Net</th><th>Early</th><th>Wear</th><th>Mainstream</th><th>Not it</th><th>Total</th></tr></thead>
            <tbody>
              {data.crowdPulse.map((brand) => (
                <tr key={brand.id}>
                  <td><Link to={`/brands/${brand.slug}`}>{brand.name}</Link></td>
                  <td>{brand.net_signal > 0 ? `+${brand.net_signal}` : brand.net_signal}</td>
                  <td>{brand.early}</td>
                  <td>{brand.would_wear}</td>
                  <td>{brand.mainstream}</td>
                  <td>{brand.not_it}</td>
                  <td>{brand.total_votes}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </section>
      )}

      {data && data.topSources.length > 0 && (
        <section style={{ marginTop: "3rem" }}>
          <h3 style={{ fontFamily: "var(--font-serif)", marginBottom: "0.75rem" }}>Best sources this week</h3>
          <table className="admin-table">
            <thead><tr><th>Source</th><th>Quality</th><th>Signals</th><th>Results</th><th>Accepted</th><th>Rejected</th></tr></thead>
            <tbody>
              {data.topSources.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.quality_score}</td>
                  <td>{s.signals_this_week}</td>
                  <td>{s.results_this_week}</td>
                  <td>{s.accepted_this_week}</td>
                  <td>{s.rejected_this_week}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {data && data.recentRejectedLeads.length > 0 && (
        <section style={{ marginTop: "3rem" }}>
          <h3 style={{ fontFamily: "var(--font-serif)", marginBottom: "0.75rem" }}>Recent rejected search leads</h3>
          <div className="admin-table-wrap"><table className="admin-table">
            <thead><tr><th>Source</th><th>Lead</th><th>Reason</th><th>When</th></tr></thead>
            <tbody>
              {data.recentRejectedLeads.map((lead) => (
                <tr key={`${lead.created_at}-${lead.message}`}>
                  <td>{lead.source_name ?? lead.source_id ?? "—"}</td>
                  <td>{lead.title ?? lead.message}</td>
                  <td>{lead.reason ?? lead.message}</td>
                  <td>{new Date(lead.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </section>
      )}

      {data && data.recentSuppressedDomains.length > 0 && (
        <section style={{ marginTop: "3rem" }}>
          <h3 style={{ fontFamily: "var(--font-serif)", marginBottom: "0.75rem" }}>Recent suppressed domains</h3>
          <div className="admin-table-wrap"><table className="admin-table">
            <thead><tr><th>Source</th><th>Domain</th><th>Lead</th><th>When</th></tr></thead>
            <tbody>
              {data.recentSuppressedDomains.map((lead) => (
                <tr key={`${lead.created_at}-${lead.message}`}>
                  <td>{lead.source_name ?? lead.source_id ?? "-"}</td>
                  <td>{lead.message}</td>
                  <td>{lead.title ?? lead.url ?? "-"}</td>
                  <td>{new Date(lead.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </section>
      )}

      {data && data.recentSourcePenalties.length > 0 && (
        <section style={{ marginTop: "3rem" }}>
          <h3 style={{ fontFamily: "var(--font-serif)", marginBottom: "0.75rem" }}>Recent source penalties</h3>
          <div className="admin-table-wrap"><table className="admin-table">
            <thead><tr><th>Source</th><th>Change</th><th>Reason</th><th>When</th></tr></thead>
            <tbody>
              {data.recentSourcePenalties.map((penalty) => (
                <tr key={`${penalty.created_at}-${penalty.message}`}>
                  <td>{penalty.sourceName ?? (penalty.sourceCount > 1 ? `${penalty.sourceCount} sources` : "-")}</td>
                  <td>{penalty.from != null && penalty.to != null ? `${penalty.from} -> ${penalty.to}` : "-"}</td>
                  <td>{penalty.message}</td>
                  <td>{new Date(penalty.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, link }: { label: string; value: number; link?: string }) {
  const inner = (
    <div className="signal-card">
      <div className="signal-card__label">{label}</div>
      <div className="signal-card__value">{value}</div>
    </div>
  );
  return link ? <Link to={link} style={{ borderBottom: "none" }}>{inner}</Link> : inner;
}

function QualityMetric({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}

function NavCard({ to, title, desc }: { to: string; title: string; desc: string }) {
  return (
    <Link to={to} className="brand-card admin-workspace-card" style={{ borderBottom: "none" }}>
      <div className="brand-card__name">{title}</div>
      <p className="brand-card__summary">{desc}</p>
    </Link>
  );
}
